import { create } from 'zustand';
import type { AgentActivityFrame, AgentActivityPlanEntry } from '../types/ws-frames';
import type { AgentActivitySnapshot } from '../lib/api';

/**
 * How many tool calls one agent keeps in one channel, matching the bound the
 * server holds its own ring to.
 *
 * The rail only ever carries upserts, never a removal, so a reader that did not
 * apply the same bound would keep entries the server has already dropped and
 * would then show a different list from the one a reload reads back.
 */
const ACTIVITY_RING_SIZE = 10;

/** What a failed entry says when the report that created it patched something never seen. */
const NEVER_OPENED_REASON = 'reported without ever being opened';

export const ACTIVITY_STATUS_PENDING = 'pending';
export const ACTIVITY_STATUS_IN_PROGRESS = 'in_progress';
export const ACTIVITY_STATUS_WAITING = 'waiting';
export const ACTIVITY_STATUS_COMPLETED = 'completed';
export const ACTIVITY_STATUS_FAILED = 'failed';

/**
 * One tool call, as the panel holds it.
 *
 * Kind and status are strings rather than the wire's unions because both enums
 * are open upstream: an unrecognised value is relayed untouched and renders as
 * the generic case rather than failing anything.
 */
export interface AgentActivityEntry {
  id: string;
  label: string;
  description: string;
  kind: string;
  status: string;
  detail: string;
  paths: string[];
  parentId: string;
  subagent: boolean;
  reason: string;
}

/**
 * One agent's work in one channel.
 *
 * `turnActive` is what the agent said through the turn boundary, never what its
 * entries imply, so an agent that finished leaving a tool call open is not
 * counted as working. `sequence` is that agent's own counter, and nothing older
 * than it is applied.
 */
export interface AgentPanelState {
  sequence: number;
  turnActive: boolean;
  plan: AgentActivityPlanEntry[];
  activities: AgentActivityEntry[];
  answer: string;
  reasoning: string;
}

/** What an agent's line says about it. Only `waiting` asks the reader to do something. */
export type AgentActivityLine = 'working' | 'waiting' | 'idle';

/** The sequence each agent had held at the moment a snapshot was asked for. */
export type AgentSequencesAtAsking = Record<string, number>;

interface AgentActivityState {
  byChannel: Record<string, Record<string, AgentPanelState>>;

  applyFrame: (frame: AgentActivityFrame) => void;
  applySnapshot: (
    channelId: string,
    snapshots: AgentActivitySnapshot[],
    askedWith: AgentSequencesAtAsking,
  ) => void;
  clearChannel: (channelId: string) => void;
  reset: () => void;
}

const EMPTY_AGENT: AgentPanelState = {
  sequence: 0,
  turnActive: false,
  plan: [],
  activities: [],
  answer: '',
  reasoning: '',
};

/**
 * Whether an agent is working, waiting on a human, or neither.
 *
 * Waiting outranks working: "an agent is waiting for you" is actionable and "an
 * agent is working" is not. Waiting can only describe a turn that is running —
 * an entry left waiting by a turn that has ended describes nothing still
 * happening, and no later report is going to move it.
 */
export function agentActivityLine(agent: AgentPanelState): AgentActivityLine {
  if (!agent.turnActive) return 'idle';
  return agent.activities.some(entry => entry.status === ACTIVITY_STATUS_WAITING) ? 'waiting' : 'working';
}

/** How many agents in one channel are on the given line. */
export function countAgentsOnLine(
  agents: Record<string, AgentPanelState> | undefined,
  line: AgentActivityLine,
): number {
  if (!agents) return 0;
  let count = 0;
  for (const agent of Object.values(agents)) {
    if (agentActivityLine(agent) === line) count += 1;
  }
  return count;
}

/** A channel's agents in a fixed order, so a report about one never reorders the others. */
export function sortAgentsById(
  agents: Record<string, AgentPanelState> | undefined,
): [string, AgentPanelState][] {
  return Object.entries(agents ?? {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

/**
 * Zips a new plan against the one held, keeping every entry that did not
 * change.
 *
 * The protocol gives plan entries no identity and requires the whole list on
 * every update, so position is all there is to match on. Rebuilding the list
 * each time would remount every row and make the section flicker at report
 * frequency; an unchanged list keeps its own reference so the section does not
 * re-render at all.
 */
function reconcilePlan(
  held: AgentActivityPlanEntry[],
  incoming: AgentActivityPlanEntry[],
): AgentActivityPlanEntry[] {
  const same =
    held.length === incoming.length &&
    held.every((entry, i) => entry.label === incoming[i].label && entry.status === incoming[i].status);
  if (same) return held;
  return incoming.map((entry, i) => {
    const previous = held[i];
    return previous && previous.label === entry.label && previous.status === entry.status ? previous : entry;
  });
}

/**
 * Opens an entry for an id nothing has opened yet, applying the same rule the
 * server does so a reload reads back what the live rail showed.
 *
 * A report carrying a label describes itself, which covers a call announcing
 * itself and equally an agent reporting one completed having never reported it
 * started. A report carrying none is a patch to something never seen, and it
 * opens as failed rather than being dropped, because agent and panel
 * disagreeing about what exists is worth showing. The exception is a patch
 * reporting a wait on a human: some tools produce no tool call at all, so their
 * permission prompt is legitimately the first thing seen about them.
 */
function openActivity(frame: AgentActivityFrame): AgentActivityEntry {
  const entry: AgentActivityEntry = {
    id: frame.activity_id ?? '',
    label: frame.label ?? '',
    description: frame.description ?? '',
    kind: frame.kind ?? '',
    status: frame.status ?? '',
    detail: frame.detail ?? '',
    paths: frame.paths ? [...frame.paths] : [],
    parentId: frame.parent_id ?? '',
    subagent: frame.subagent === true,
    reason: frame.reason ?? '',
  };
  if (entry.label === '' && entry.status !== ACTIVITY_STATUS_WAITING) {
    entry.status = ACTIVITY_STATUS_FAILED;
    if (entry.reason === '') entry.reason = NEVER_OPENED_REASON;
  } else if (entry.status === '') {
    entry.status = ACTIVITY_STATUS_PENDING;
  }
  return entry;
}

/**
 * Folds a report into an entry that already exists, answering the same entry
 * when it says nothing new.
 *
 * An absent field means unchanged, and on this wire an absent field and an
 * empty one arrive the same way, so both leave what is there alone. Paths
 * replace rather than merge, because an agent streaming a tool's input resends
 * the whole list. No status is refused: the protocol declares no transition
 * table, so whatever was last reported is what the entry says.
 */
function patchActivity(held: AgentActivityEntry, frame: AgentActivityFrame): AgentActivityEntry {
  const patched: AgentActivityEntry = {
    ...held,
    label: frame.label || held.label,
    description: frame.description || held.description,
    kind: frame.kind || held.kind,
    status: frame.status || held.status,
    detail: frame.detail || held.detail,
    // Being a container is a fact about the call and no producer un-says it: a
    // later report that omits it had nothing to add.
    subagent: held.subagent || frame.subagent === true,
    parentId: frame.parent_id || held.parentId,
    reason: frame.reason || held.reason,
  };
  if (frame.paths) patched.paths = [...frame.paths];

  const unchanged =
    patched.label === held.label &&
    patched.description === held.description &&
    patched.kind === held.kind &&
    patched.status === held.status &&
    patched.detail === held.detail &&
    patched.subagent === held.subagent &&
    patched.parentId === held.parentId &&
    patched.reason === held.reason &&
    patched.paths.length === held.paths.length &&
    patched.paths.every((path, i) => path === held.paths[i]);
  return unchanged ? held : patched;
}

/**
 * Brings the ring back inside its bound, taking the oldest finished entry first
 * so a tool running for the whole turn is never dropped for chatter that
 * finished after it started. When nothing has finished the oldest goes anyway:
 * the bound is what makes this state safe to keep.
 */
function evict(entries: AgentActivityEntry[]): AgentActivityEntry[] {
  let kept = entries;
  while (kept.length > ACTIVITY_RING_SIZE) {
    const finished = kept.findIndex(
      entry => entry.status === ACTIVITY_STATUS_COMPLETED || entry.status === ACTIVITY_STATUS_FAILED,
    );
    const victim = finished === -1 ? 0 : finished;
    kept = [...kept.slice(0, victim), ...kept.slice(victim + 1)];
  }
  return kept;
}

function upsertActivity(
  entries: AgentActivityEntry[],
  frame: AgentActivityFrame,
): AgentActivityEntry[] {
  const index = entries.findIndex(entry => entry.id === (frame.activity_id ?? ''));
  if (index === -1) return evict([...entries, openActivity(frame)]);
  const patched = patchActivity(entries[index], frame);
  if (patched === entries[index]) return entries;
  const next = entries.slice();
  next[index] = patched;
  return next;
}

/**
 * Folds one report into an agent's state, answering the same state when the
 * report changes nothing a reader could see.
 *
 * Identity is what stops the panel re-rendering at report frequency: the
 * producer coalesces to the newest value and sends absolute state, so the same
 * output tail arriving twice is ordinary rather than exceptional.
 */
function foldFrame(agent: AgentPanelState, frame: AgentActivityFrame): AgentPanelState {
  switch (frame.shape) {
    case 'activity': {
      const activities = upsertActivity(agent.activities, frame);
      return activities === agent.activities ? agent : { ...agent, sequence: frame.sequence, activities };
    }
    case 'plan': {
      const plan = reconcilePlan(agent.plan, frame.plan ?? []);
      return plan === agent.plan ? agent : { ...agent, sequence: frame.sequence, plan };
    }
    case 'output': {
      const text = frame.text ?? '';
      if (frame.stream === 'reasoning') {
        return text === agent.reasoning ? agent : { ...agent, sequence: frame.sequence, reasoning: text };
      }
      if (frame.stream === 'answer') {
        return text === agent.answer ? agent : { ...agent, sequence: frame.sequence, answer: text };
      }
      return agent;
    }
    case 'turn': {
      const turnActive = frame.turn_state === 'started';
      return turnActive === agent.turnActive ? agent : { ...agent, sequence: frame.sequence, turnActive };
    }
    default:
      return agent;
  }
}

/** Restates one agent's snapshot in the shape the live rail folds into. */
function fromSnapshot(snapshot: AgentActivitySnapshot): AgentPanelState {
  return {
    sequence: snapshot.sequence,
    // The server's own line is the inverse of the same rule: it reads idle from
    // a turn that is not running and nothing else, so idle is exactly a turn
    // that has ended.
    turnActive: snapshot.state !== 'idle',
    plan: snapshot.plan ? snapshot.plan.map(entry => ({ ...entry })) : [],
    activities: (snapshot.activities ?? []).map(entry => ({
      id: entry.id,
      label: entry.label ?? '',
      description: entry.description ?? '',
      kind: entry.kind ?? '',
      status: entry.status,
      detail: entry.detail ?? '',
      paths: entry.paths ? [...entry.paths] : [],
      parentId: entry.parent_id ?? '',
      subagent: entry.subagent === true,
      reason: entry.reason ?? '',
    })),
    answer: snapshot.answer ?? '',
    reasoning: snapshot.reasoning ?? '',
  };
}

/**
 * What every agent is doing in every channel the reader can see, held for as
 * long as it is worth looking at and written nowhere.
 *
 * The strip is the only subscriber. Nothing that renders the message list may
 * read this store: `MessageList` is not memoised and rebuilds the identity that
 * busts every row's memo, so an activity report reaching the channel view would
 * cost a full-channel markdown re-parse several times a second.
 */
export const useAgentActivityStore = create<AgentActivityState>()((set) => ({
  byChannel: {},

  applyFrame: (frame) => set((s) => {
    const agents = s.byChannel[frame.channel_id];
    const agent = agents?.[frame.agent_id];
    // The bus guarantees nothing about order or duplication, so anything not
    // newer than what this agent already said is not applied at all.
    if (agent && frame.sequence <= agent.sequence) return s;

    // An agent heard from for the first time takes this report's counter even
    // when the report itself changes nothing, or the next stale report would
    // measure itself against a counter that never started.
    const folded = foldFrame(agent ?? { ...EMPTY_AGENT, sequence: frame.sequence }, frame);
    if (agent && folded === agent) return s;

    return {
      byChannel: {
        ...s.byChannel,
        [frame.channel_id]: { ...agents, [frame.agent_id]: folded },
      },
    };
  }),

  /**
   * Takes a channel's whole answer, keeping only what an agent has said since
   * the question was asked.
   *
   * The socket subscribes to every channel the reader belongs to long before
   * one is opened, so reports routinely land while this request is in flight.
   * Comparing sequences would not settle that on its own: an agent whose plugin
   * reconnected starts its counter again, so a lower number is as likely to
   * mean "a new connection" as "an older report". What the answer cannot know
   * about is exactly what arrived after it was asked for, so that is what is
   * kept and everything else is replaced.
   *
   * An agent this answer does not carry is one the server has forgotten — its
   * connection ended, or it fell silent past the expiry window — and it goes,
   * under the same rule. Nothing else would ever drop it: the rail carries
   * upserts and never a removal.
   */
  applySnapshot: (channelId, snapshots, askedWith) => set((s) => {
    const held = s.byChannel[channelId] ?? {};
    const spokeSinceAsking = (agentId: string) =>
      held[agentId] !== undefined && held[agentId].sequence !== askedWith[agentId];

    const next: Record<string, AgentPanelState> = {};
    for (const snapshot of snapshots) {
      next[snapshot.agent_id] = spokeSinceAsking(snapshot.agent_id)
        ? held[snapshot.agent_id]
        : fromSnapshot(snapshot);
    }
    for (const agentId of Object.keys(held)) {
      if (next[agentId] === undefined && spokeSinceAsking(agentId)) next[agentId] = held[agentId];
    }

    const heldIds = Object.keys(held);
    const unchanged =
      heldIds.length === Object.keys(next).length &&
      heldIds.every(agentId => next[agentId] === held[agentId]);
    if (unchanged) return s;

    return { byChannel: { ...s.byChannel, [channelId]: next } };
  }),

  clearChannel: (channelId) => set((s) => {
    if (!s.byChannel[channelId]) return s;
    const { [channelId]: _dropped, ...rest } = s.byChannel;
    return { byChannel: rest };
  }),

  reset: () => set({ byChannel: {} }),
}));
